import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { PanelDomainSslCard } from "../components/PanelDomainSslCard";
import type { PanelUser } from "../types";

export type SslOverviewRow = {
  siteId: string;
  userId: string;
  username?: string | null;
  domain: string;
  type: string;
  status: string;
  sslEnabled: boolean;
  forceHttps: boolean;
  sslProvider: string | null;
  sslStatus: string;
  sslExpiresAt: string | null;
  sslError: string | null;
  certificateDomains: string[];
  keyType: string;
  createdOn: string | null;
};

type SslTab = "certs" | "logs";

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / (1000 * 60 * 60 * 24));
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  } catch {
    return iso.slice(0, 10);
  }
}

/**
 * Premium SSL / TLS management (redesign_part3.md).
 */
export function AutomaticSslPage({
  currentUser,
  isStaff,
  initialTab
}: {
  currentUser: PanelUser;
  isStaff: boolean;
  initialTab?: "certs" | "logs";
}) {
  const [items, setItems] = useState<SslOverviewRow[]>([]);
  const [tab, setTab] = useState<SslTab>(initialTab || "certs");
  const [selected, setSelected] = useState<string[]>([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [logs, setLogs] = useState<
    {
      time: string;
      domain: string;
      action: string;
      status: "ok" | "fail";
      detail: string;
    }[]
  >([]);
  const [page, setPage] = useState(1);
  const perPage = 50;
  const [menuId, setMenuId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SslOverviewRow | null>(null);
  const [installTarget, setInstallTarget] = useState<SslOverviewRow[] | null>(
    null
  );
  const [revokeTarget, setRevokeTarget] = useState<SslOverviewRow | null>(
    null
  );
  const [installProgress, setInstallProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api<{
        ok: boolean;
        items: SslOverviewRow[];
        keyType?: string;
      }>("/api/v1/ssl/overview");
      setItems(res.items || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const classify = (row: SslOverviewRow): string => {
    if (!row.sslEnabled || row.sslStatus === "none") return "none";
    if (row.sslStatus === "failed") return "failed";
    if (row.sslStatus === "expired") return "expired";
    if (row.sslStatus === "expiring") return "expiring";
    if (row.sslStatus === "pending" || row.sslStatus === "renewing")
      return "pending";
    const d = daysUntil(row.sslExpiresAt);
    if (d != null && d < 0) return "expired";
    if (d != null && d <= 14) return "expiring";
    return "active";
  };

  const health = useMemo(() => {
    let protectedN = 0;
    let expiring = 0;
    let missing = 0;
    let https = 0;
    let autoRenew = 0;
    for (const i of items) {
      const c = classify(i);
      if (c === "active" || c === "expiring") {
        protectedN++;
        autoRenew++;
      }
      if (c === "expiring") expiring++;
      if (c === "none" || c === "failed" || c === "expired") missing++;
      if (i.forceHttps && i.sslEnabled) https++;
    }
    return {
      protectedN,
      expiring,
      missing,
      https,
      autoRenew,
      total: items.length
    };
  }, [items]);

  const filtered = useMemo(() => {
    let list = [...items];
    const s = q.trim().toLowerCase();
    if (s) {
      list = list.filter(
        (i) =>
          i.domain.toLowerCase().includes(s) ||
          (i.username || "").toLowerCase().includes(s) ||
          (i.sslProvider || "").toLowerCase().includes(s)
      );
    }
    if (statusFilter !== "all") {
      list = list.filter((i) => classify(i) === statusFilter);
    }
    return list;
  }, [items, q, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageItems = filtered.slice((page - 1) * perPage, page * perPage);

  const toggle = (id: string) => {
    setSelected((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
    );
  };

  const toggleAll = () => {
    if (selected.length === pageItems.length) setSelected([]);
    else setSelected(pageItems.map((i) => i.siteId));
  };

  const pushLog = (
    domain: string,
    action: string,
    status: "ok" | "fail",
    detail: string
  ) => {
    setLogs((L) =>
      [
        {
          time: new Date().toISOString().slice(0, 19).replace("T", " "),
          domain,
          action,
          status,
          detail
        },
        ...L
      ].slice(0, 200)
    );
  };

  const issueOne = async (siteId: string, domain: string) => {
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const res = await api<{
        ok: boolean;
        result?: { note?: string; provider?: string };
      }>(`/api/v1/sites/${siteId}/ssl/issue`, {
        method: "POST",
        body: JSON.stringify({ forceHttps: true })
      });
      const note = res.result?.note;
      const provider = res.result?.provider || "letsencrypt";
      setMsg(
        note
          ? `SSL for ${domain}: ${note}`
          : `SSL installed for ${domain} (${provider}, RSA 2048-bit)`
      );
      pushLog(domain, "Install", "ok", `${provider}, RSA 2048`);
      await load();
    } catch (err) {
      const m = err instanceof Error ? err.message : String(err);
      const friendly = /aborted|timeout|AGENT_TIMEOUT/i.test(m)
        ? `${m} — wait and retry. Certbot often needs 60–120s; DNS must point to this server.`
        : m;
      setError(`${domain}: ${friendly}`);
      pushLog(domain, "Install", "fail", friendly);
    } finally {
      setBusy(false);
    }
  };

  const revokeOne = async (siteId: string, domain: string) => {
    setBusy(true);
    setError("");
    setMsg("");
    setRevokeTarget(null);
    try {
      await api(`/api/v1/sites/${siteId}/ssl/revoke`, {
        method: "POST",
        body: "{}"
      });
      setMsg(`SSL revoked for ${domain}`);
      pushLog(domain, "Revoke", "ok", "revoked");
      setDetail(null);
      await load();
    } catch (err) {
      const m = err instanceof Error ? err.message : String(err);
      setError(m);
      pushLog(domain, "Revoke", "fail", m);
    } finally {
      setBusy(false);
    }
  };

  const renewOne = async (siteId: string, domain: string) => {
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await api("/api/v1/ssl/renew", {
        method: "POST",
        body: JSON.stringify({ siteId, dryRun: false })
      });
      setMsg(`SSL renewed for ${domain}`);
      pushLog(domain, "Renew", "ok", "renewed");
      await load();
    } catch (err) {
      const m = err instanceof Error ? err.message : String(err);
      setError(m);
      pushLog(domain, "Renew", "fail", m);
    } finally {
      setBusy(false);
    }
  };

  const runInstall = async (targets: SslOverviewRow[]) => {
    if (!targets.length) return;
    setBusy(true);
    setError("");
    setMsg("");
    setInstallProgress({ done: 0, total: targets.length });
    try {
      if (targets.length > 1) {
        const res = await api<{
          installed: string[];
          failed: { domain: string; error: string }[];
        }>("/api/v1/ssl/issue-all", {
          method: "POST",
          body: JSON.stringify({
            siteIds: targets.map((t) => t.siteId),
            forceHttps: true
          })
        });
        setMsg(
          `Installed ${res.installed?.length || 0} · failed ${res.failed?.length || 0}`
        );
        for (const d of res.installed || [])
          pushLog(d, "Install", "ok", "RSA 2048");
        for (const f of res.failed || [])
          pushLog(f.domain, "Install", "fail", f.error);
        setInstallProgress({
          done: targets.length,
          total: targets.length
        });
      } else {
        await issueOne(targets[0].siteId, targets[0].domain);
        setInstallProgress({ done: 1, total: 1 });
      }
      setSelected([]);
      setInstallTarget(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      setTimeout(() => setInstallProgress(null), 1500);
    }
  };

  const openBulkInstall = () => {
    const ids =
      selected.length > 0
        ? selected
        : items
            .filter((i) => !i.sslEnabled || i.sslStatus === "failed")
            .map((i) => i.siteId);
    const targets = items.filter((i) => ids.includes(i.siteId));
    if (!targets.length) {
      setError(
        selected.length
          ? "No domains selected"
          : "No domains need SSL (or select domains first)"
      );
      return;
    }
    setInstallTarget(targets);
  };

  const statusBadge = (row: SslOverviewRow) => {
    const c = classify(row);
    if (c === "none")
      return (
        <span className="rp-status rp-status-muted">Not Installed</span>
      );
    if (c === "failed")
      return (
        <span className="rp-status rp-status-crit">
          <span className="rp-status-dot" />
          Renewal Failed
        </span>
      );
    if (c === "expired")
      return (
        <span className="rp-status rp-status-crit">
          <span className="rp-status-dot" />
          Expired
        </span>
      );
    if (c === "expiring")
      return (
        <span className="rp-status rp-status-warn">
          <span className="rp-status-dot" />
          Expiring Soon
        </span>
      );
    if (c === "pending")
      return (
        <span className="rp-status rp-status-info">
          <span className="rp-status-dot" />
          Pending
        </span>
      );
    return (
      <span className="rp-status rp-status-ok">
        <span className="rp-status-dot" />
        Active
      </span>
    );
  };

  const expiryLabel = (row: SslOverviewRow) => {
    const d = daysUntil(row.sslExpiresAt);
    if (d == null) return null;
    if (d < 0)
      return (
        <span className="rp-status rp-status-crit" style={{ fontSize: 11 }}>
          Expired {Math.abs(d)}d ago
        </span>
      );
    if (d <= 7)
      return (
        <span className="rp-status rp-status-crit" style={{ fontSize: 11 }}>
          {d} day{d === 1 ? "" : "s"} left
        </span>
      );
    if (d <= 30)
      return (
        <span className="rp-status rp-status-warn" style={{ fontSize: 11 }}>
          {d} days left
        </span>
      );
    return (
      <span className="faint" style={{ fontSize: 11 }}>
        {d} days
      </span>
    );
  };

  return (
    <div className="rp-page">
      <div className="rp-page-head">
        <div>
          <h1 className="rp-page-title">SSL / TLS</h1>
          <p className="rp-page-sub">
            Manage certificates, HTTPS, renewals and certificate health.
          </p>
          <p className="rp-sum-hint" style={{ marginTop: 6 }}>
            Let&apos;s Encrypt · RSA 2048-bit
            {isStaff
              ? " · all domains on this server"
              : " · your account domains"}
          </p>
        </div>
        <div className="rp-page-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void load()}
            disabled={busy}
          >
            Refresh
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: "auto", marginTop: 0 }}
            disabled={busy}
            onClick={openBulkInstall}
          >
            Install Certificates
          </button>
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      {(isStaff ||
        currentUser.role === "reseller") && (
        <PanelDomainSslCard
          isStaff={isStaff}
          isReseller={currentUser.role === "reseller"}
        />
      )}

      <div className="rp-summary-strip" style={{ marginBottom: 14 }}>
        <div className="rp-sum">
          <span className="rp-sum-label">Protected</span>
          <span className="rp-sum-value">{health.protectedN}</span>
          <span className="rp-sum-hint">Active certificates</span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">Expiring Soon</span>
          <span className="rp-sum-value">{health.expiring}</span>
          <span className="rp-sum-hint">Within ~14 days</span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">Missing SSL</span>
          <span className="rp-sum-value">{health.missing}</span>
          <span className="rp-sum-hint">Need install / repair</span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">Auto-Renewal</span>
          <span className="rp-sum-value" style={{ fontSize: 16 }}>
            {health.autoRenew} / {health.total || 0}
          </span>
          <span className="rp-sum-hint">
            {health.total ? "Enrolled" : "No domains"}
          </span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">HTTPS</span>
          <span className="rp-sum-value" style={{ fontSize: 16 }}>
            {health.https} / {health.total || 0}
          </span>
          <span className="rp-sum-hint">Force redirect on</span>
        </div>
      </div>

      <div className="rp-panel" style={{ padding: 14, marginBottom: 14 }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "center"
          }}
        >
          <strong style={{ fontSize: 13 }}>Automatic Renewal</strong>
          <span className="rp-status rp-status-ok">
            <span className="rp-status-dot" />
            Enabled
          </span>
          <span className="faint" style={{ fontSize: 13 }}>
            {health.autoRenew} of {health.total || 0} certificates enrolled in
            automatic renewal.
          </span>
        </div>
      </div>

      <div className="rp-pkg-tabs">
        <button
          type="button"
          className={`rp-pkg-tab${tab === "certs" ? " is-active" : ""}`}
          onClick={() => setTab("certs")}
        >
          Certificates
        </button>
        <button
          type="button"
          className={`rp-pkg-tab${tab === "logs" ? " is-active" : ""}`}
          onClick={() => setTab("logs")}
        >
          Certificate Logs
        </button>
      </div>

      {tab === "certs" ? (
        <>
          <div className="rp-toolbar">
            <input
              className="rp-toolbar-search"
              placeholder="Search domain…"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
            />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="expiring">Expiring soon</option>
              <option value="expired">Expired</option>
              <option value="none">Not installed</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </select>
            <span className="rp-bulk-hint">
              {filtered.length} result{filtered.length === 1 ? "" : "s"}
              {selected.length ? ` · ${selected.length} selected` : ""}
            </span>
            {selected.length > 0 ? (
              <div className="rp-bulk-bar">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  style={{ width: "auto", marginTop: 0 }}
                  disabled={busy}
                  onClick={() => {
                    const targets = items.filter((i) =>
                      selected.includes(i.siteId)
                    );
                    setInstallTarget(targets);
                  }}
                >
                  Install SSL ({selected.length})
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={busy}
                  onClick={async () => {
                    for (const id of selected) {
                      const row = items.find((i) => i.siteId === id);
                      if (row?.sslEnabled)
                        await renewOne(id, row.domain);
                    }
                    setSelected([]);
                  }}
                >
                  Renew
                </button>
              </div>
            ) : null}
          </div>

          <div className="rp-panel">
            <table className="rp-acct-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      checked={
                        pageItems.length > 0 &&
                        selected.length === pageItems.length
                      }
                      onChange={toggleAll}
                      aria-label="Select page"
                    />
                  </th>
                  <th>Domain</th>
                  {isStaff ? <th>Account</th> : null}
                  <th>Certificate</th>
                  <th>Expires</th>
                  <th>Coverage</th>
                  <th>HTTPS</th>
                  <th>Status</th>
                  <th style={{ width: 100 }} />
                </tr>
              </thead>
              <tbody>
                {pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={isStaff ? 9 : 8}>
                      <div className="rp-empty">
                        <strong>No domains yet</strong>
                        <p>
                          Add a website first, then install SSL certificates
                          here.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  pageItems.map((row) => (
                    <tr key={row.siteId}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.includes(row.siteId)}
                          onChange={() => toggle(row.siteId)}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            textAlign: "left",
                            font: "inherit",
                            color: "inherit"
                          }}
                          onClick={() => setDetail(row)}
                        >
                          <strong style={{ fontWeight: 650 }}>
                            {row.domain}
                          </strong>
                        </button>
                        {row.sslError ? (
                          <div
                            className="faint"
                            style={{ fontSize: 11, maxWidth: 220 }}
                          >
                            {row.sslError.slice(0, 80)}
                          </div>
                        ) : null}
                      </td>
                      {isStaff ? (
                        <td className="mono faint">
                          {row.username || "—"}
                        </td>
                      ) : null}
                      <td>
                        <div style={{ fontSize: 13 }}>
                          {row.sslEnabled
                            ? row.sslProvider || "Let's Encrypt"
                            : "—"}
                        </div>
                        <div className="faint" style={{ fontSize: 11 }}>
                          {row.keyType || "RSA 2048"}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: 13 }}>
                          {formatDate(row.sslExpiresAt)}
                        </div>
                        {expiryLabel(row)}
                      </td>
                      <td className="faint" style={{ fontSize: 13 }}>
                        {row.certificateDomains?.length
                          ? `${row.certificateDomains.length} domain${
                              row.certificateDomains.length === 1 ? "" : "s"
                            }`
                          : "—"}
                      </td>
                      <td>
                        <span
                          className={`rp-status ${
                            row.forceHttps && row.sslEnabled
                              ? "rp-status-ok"
                              : "rp-status-muted"
                          }`}
                        >
                          {row.forceHttps && row.sslEnabled
                            ? "Enabled"
                            : "Off"}
                        </span>
                      </td>
                      <td>{statusBadge(row)}</td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            gap: 4,
                            justifyContent: "flex-end"
                          }}
                        >
                          {!row.sslEnabled ||
                          row.sslStatus === "failed" ||
                          row.sslStatus === "none" ? (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              disabled={busy}
                              onClick={() => setInstallTarget([row])}
                            >
                              Install
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              disabled={busy}
                              onClick={() => setDetail(row)}
                            >
                              Details
                            </button>
                          )}
                          <div className="rp-menu-wrap">
                            <button
                              type="button"
                              className="rp-menu-btn"
                              onClick={() =>
                                setMenuId((id) =>
                                  id === row.siteId ? null : row.siteId
                                )
                              }
                            >
                              •••
                            </button>
                            {menuId === row.siteId ? (
                              <div className="rp-menu">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMenuId(null);
                                    setInstallTarget([row]);
                                  }}
                                >
                                  Install certificate
                                </button>
                                <button
                                  type="button"
                                  disabled={!row.sslEnabled}
                                  onClick={() => {
                                    setMenuId(null);
                                    void renewOne(row.siteId, row.domain);
                                  }}
                                >
                                  Renew
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMenuId(null);
                                    setDetail(row);
                                  }}
                                >
                                  View details
                                </button>
                                <button
                                  type="button"
                                  disabled={!row.sslEnabled}
                                  onClick={() => {
                                    setMenuId(null);
                                    setRevokeTarget(row);
                                  }}
                                >
                                  Revoke certificate
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: 12,
                alignItems: "center"
              }}
            >
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ‹
              </button>
              <span className="faint" style={{ fontSize: 13 }}>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                ›
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <div className="rp-panel">
          <table className="rp-acct-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Domain</th>
                <th>Action</th>
                <th>Status</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="faint">
                    No actions yet. Install / Renew / Revoke will appear here.
                  </td>
                </tr>
              ) : (
                logs.map((l, i) => (
                  <tr key={i}>
                    <td className="mono faint" style={{ fontSize: 12 }}>
                      {l.time}
                    </td>
                    <td className="mono">{l.domain}</td>
                    <td>{l.action}</td>
                    <td>
                      <span
                        className={`rp-status ${
                          l.status === "ok"
                            ? "rp-status-ok"
                            : "rp-status-crit"
                        }`}
                      >
                        {l.status === "ok" ? "Success" : "Failed"}
                      </span>
                    </td>
                    <td
                      className="faint"
                      style={{ fontSize: 12, maxWidth: 320 }}
                    >
                      {l.detail}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="faint" style={{ marginTop: 12, fontSize: 12 }}>
        Signed in as <strong>{currentUser.username}</strong>
        {isStaff ? " (admin)" : " (user)"} · Automatic SSL uses{" "}
        <strong>RSA 2048-bit</strong> keys.
      </p>

      {/* Certificate details drawer */}
      {detail ? (
        <div className="rp-drawer-bg" onClick={() => setDetail(null)}>
          <div
            className="rp-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Certificate details"
          >
            <div className="rp-drawer-hd">
              <div>
                <h2>{detail.domain}</h2>
                <p>Certificate details and actions</p>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setDetail(null)}
              >
                ✕
              </button>
            </div>
            <div className="rp-drawer-body">
              <div className="rp-drawer-section">
                <div style={{ marginBottom: 10 }}>{statusBadge(detail)}</div>
                <dl className="rp-health-meta">
                  <div>
                    <dt>Certificate</dt>
                    <dd>
                      {detail.sslProvider || "Let's Encrypt"} ·{" "}
                      {detail.keyType || "RSA 2048"}
                    </dd>
                  </div>
                  <div>
                    <dt>Issued</dt>
                    <dd>{formatDate(detail.createdOn)}</dd>
                  </div>
                  <div>
                    <dt>Expires</dt>
                    <dd>
                      {formatDate(detail.sslExpiresAt)}{" "}
                      {expiryLabel(detail)}
                    </dd>
                  </div>
                  <div>
                    <dt>HTTPS redirect</dt>
                    <dd>
                      {detail.forceHttps && detail.sslEnabled
                        ? "Enabled"
                        : "Off"}
                    </dd>
                  </div>
                  <div>
                    <dt>Auto-renew</dt>
                    <dd>
                      {detail.sslEnabled ? "Enabled" : "Not enrolled"}
                    </dd>
                  </div>
                </dl>
                {detail.certificateDomains?.length ? (
                  <>
                    <h3 style={{ marginTop: 14 }}>Covered domains</h3>
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: 18,
                        fontSize: 13
                      }}
                    >
                      {detail.certificateDomains.map((d) => (
                        <li key={d} className="mono">
                          {d}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
                {detail.sslError ? (
                  <p
                    className="error-banner"
                    style={{ marginTop: 12, fontSize: 12 }}
                  >
                    {detail.sslError}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="rp-drawer-ft">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy || !detail.sslEnabled}
                onClick={() => setRevokeTarget(detail)}
              >
                Revoke
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy || !detail.sslEnabled}
                onClick={() => void renewOne(detail.siteId, detail.domain)}
              >
                Renew
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: "auto", marginTop: 0 }}
                disabled={busy}
                onClick={() => {
                  setDetail(null);
                  setInstallTarget([detail]);
                }}
              >
                Install Certificate
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Install confirmation drawer */}
      {installTarget ? (
        <div
          className="rp-drawer-bg"
          onClick={() => !busy && setInstallTarget(null)}
        >
          <div
            className="rp-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Install SSL"
            style={{ maxWidth: 440 }}
          >
            <div className="rp-drawer-hd">
              <div>
                <h2>
                  {installTarget.length === 1
                    ? "Install SSL Certificate"
                    : `Install ${installTarget.length} Certificates?`}
                </h2>
                <p>Let&apos;s Encrypt · RSA 2048-bit · force HTTPS</p>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={busy}
                onClick={() => setInstallTarget(null)}
              >
                ✕
              </button>
            </div>
            <div className="rp-drawer-body">
              <div className="rp-drawer-section">
                <h3>Domains</h3>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 18,
                    fontSize: 13,
                    maxHeight: 160,
                    overflow: "auto"
                  }}
                >
                  {installTarget.map((t) => (
                    <li key={t.siteId} className="mono">
                      {t.domain}
                    </li>
                  ))}
                </ul>
                <dl className="rp-health-meta" style={{ marginTop: 14 }}>
                  <div>
                    <dt>Certificate</dt>
                    <dd>Let&apos;s Encrypt</dd>
                  </div>
                  <div>
                    <dt>Key</dt>
                    <dd>RSA 2048-bit</dd>
                  </div>
                  <div>
                    <dt>HTTPS</dt>
                    <dd>Redirect HTTP → HTTPS</dd>
                  </div>
                  <div>
                    <dt>Auto-renew</dt>
                    <dd>Enabled</dd>
                  </div>
                </dl>
                {installProgress ? (
                  <p style={{ marginTop: 12, fontSize: 13 }}>
                    Progress: {installProgress.done} / {installProgress.total}{" "}
                    completed
                  </p>
                ) : null}
              </div>
            </div>
            <div className="rp-drawer-ft">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => setInstallTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: "auto", marginTop: 0 }}
                disabled={busy}
                onClick={() => void runInstall(installTarget)}
              >
                {busy
                  ? "Installing…"
                  : installTarget.length === 1
                    ? "Install Certificate"
                    : "Install Certificates"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Revoke confirmation */}
      {revokeTarget ? (
        <div
          className="rp-drawer-bg"
          onClick={() => !busy && setRevokeTarget(null)}
        >
          <div
            className="rp-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Revoke certificate"
            style={{ maxWidth: 400 }}
          >
            <div className="rp-drawer-hd">
              <div>
                <h2>Revoke certificate?</h2>
                <p>
                  HTTPS may stop working for{" "}
                  <strong>{revokeTarget.domain}</strong> and covered domains.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setRevokeTarget(null)}
              >
                ✕
              </button>
            </div>
            <div className="rp-drawer-ft">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setRevokeTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{
                  width: "auto",
                  marginTop: 0,
                  background: "var(--danger)",
                  borderColor: "var(--danger)"
                }}
                disabled={busy}
                onClick={() =>
                  void revokeOne(revokeTarget.siteId, revokeTarget.domain)
                }
              >
                {busy ? "Revoking…" : "Revoke Certificate"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
